import { useCallback, useState } from 'react';
import { Button } from '~src/components/Button';
import { InfoCard } from '~src/components/common';
import { useRouter } from 'next/router';
import { routes } from '../../../../../../src/routes/routes';
import Modal from '~src/components/Modal';
import Products from '~src/components/Store/Products/Products';
import Link from '~src/components/Link';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';

export default function DashboardNodes({
  walletItems,
  setShowSetupNode,
  isLoading,
  smartNodes,
  liteNodes,
  licenses,
  products,
  storeError,
  cartError,
  cartItems
}) {
  const router = useRouter();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const ethCoin = walletItems.find((coin) => coin.coinSymbol === 'ETH');
  const isWalletSetup = !!ethCoin?.walletId;

  const smartNodeLicenses = licenses.filter((l) => l.licenseType === 'smart');
  const liteNodeLicenses = licenses.filter((l) => l.licenseType === 'lite');

  const activeSmartNodes = smartNodes.filter((n) => n.online === true);
  // Each active lite node counts for up to 100 lite node licenses
  const activeLiteNodes = Math.min((liteNodes.filter((n) => n.online === true).length) * 100, liteNodeLicenses.length);

  const openPurchaseModal = (e) => {
    e ? e.preventDefault() : null;
    setShowPurchaseModal(true);
  };

  const proceedToCart = () => {
    !cartError && router.push(routes.cart.path);
  }

  const setupNodes = useCallback(() => {
    setShowSetupNode(true);
  }, [setShowSetupNode]);

  const handleGoToCart = () => {
    cartItems.length > 0 && router.push(routes.cart.path)
  }

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-row sm:justify-end mb-6 w-full gap-4 items-center">
        {!isWalletSetup && (
          <Link
            href="/setup-wallet"
            className="bg-brand-200 hover:bg-brand-100 font-medium text-brandGray-900 px-4 py-2 rounded-lg justify-center items-center"
          >
            <span className="whitespace-nowrap">Set up Wallet</span>
          </Link>
        )}
        {isWalletSetup && (
          <div className='flex w-8 h-8'>
            <button onClick={handleGoToCart} className={cartItems.length > 0 ? 'cursor-pointer' : 'cursor-default'}>
            {cartItems.length > 0 && <div className='rounded-full w-3 h-3 bg-red-500 relative top-[11px] left-5 border-2 border-solid border-gray-100 -mt-3'></div>}
              <ShoppingCartIcon className={'h-7 w-7 text-gray-400'}/>
            </button>
          </div>
        )}
        {licenses.length > 0 && isWalletSetup && (
          <Button
            onClick={licenses.length > 0 ? setupNodes : null}
            variant="secondary"
            data-brand-qa="node-setup-btn"
            width="w-full md:w-28"
            size="sm"
          >
            <span className="whitespace-nowrap font-bold">Node Setup</span>
          </Button>
        )}
        {isWalletSetup && (
          <Button
            // className="ml-4"
            width="w-full md:w-40"
            data-brand-qa="purchase-nodes-btn"
            onClick={openPurchaseModal}
            size='sm'
          >
            <span className="whitespace-nowrap my-0.5">Purchase Nodes</span>
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <InfoCard
          amountOne={activeSmartNodes.length}
          amountTwo={smartNodeLicenses.length}
          isLoading={isLoading}
          headingText={'Smart'}
          dataTag={'smart-nodes'}
          type="smart"
        />
        <InfoCard
          amountOne={activeLiteNodes}
          amountTwo={liteNodeLicenses.length}
          isLoading={isLoading}
          headingText={'Lite'}
          dataTag={'lite-nodes'}
          type="lite"
          tooltipContent={
            <div>Each &quot;online&quot; machine in the &quot;Lite Nodes&quot; <br/> table counts for up to 100 lite node licenses</div>
          }
        />
        <InfoCard
          amountOne={activeSmartNodes.length + activeLiteNodes}
          amountTwo={smartNodeLicenses.length + liteNodeLicenses.length}
          isLoading={isLoading}
          headingText={'Total'}
          dataTag={'total-nodes'}
          type="smart"
        />
      </div>
      {showPurchaseModal && (
        <Modal
          title="Purchase Nodes"
          onClose={() => setShowPurchaseModal(false)}
          showClose
          canCloseOnClickOut={true}
        >
          {storeError ? (
            <div className='p-2 flex flex-col gap-8 items-center w-full mb-6'>
              <span>Oops! Error loading products.</span>
            </div>
          ) : (
            <div className='px-2'>
              <Products products={products} error={storeError} cartItems={cartItems} />
              <hr className='my-6 -mx-6'/>
              <div className='flex justify-between mb-4 items-center'>
                <a href='mailto:support@lemonaide.co' className='text-mint-600 ml-2'>Need Help?</a>
                <Button
                  width='w-44'
                  onClick={proceedToCart}
                  data-brand-qa="continue-to-cart"
                  disabled={cartError}
                >
                  Continue to Cart
                </Button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
